const STORAGE_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  idToken: 'idToken',
} as const;

// In-memory cache — avoids repeated localStorage reads and provides
// a single source of truth for the current session tokens.
let accessToken: string | null = null;
let refreshToken: string | null = null;
let idToken: string | null = null;

// Hydrate from localStorage on module load so that a page reload
// doesn't lose the session before Cognito SDK re-validates it.
function hydrate() {
  if (typeof window === 'undefined') return;

  try {
    accessToken = window.localStorage.getItem(STORAGE_KEYS.accessToken);
    refreshToken = window.localStorage.getItem(STORAGE_KEYS.refreshToken);
    idToken = window.localStorage.getItem(STORAGE_KEYS.idToken);
  } catch {
    accessToken = null;
    refreshToken = null;
    idToken = null;
  }
}

hydrate();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function getAccessToken(): string | null {
  return accessToken;
}

function getRefreshToken(): string | null {
  return refreshToken;
}

function getIdToken(): string | null {
  return idToken;
}

function setTokens(access: string, refresh: string, id: string): void {
  accessToken = access;
  refreshToken = refresh;
  idToken = id;

  try {
    window.localStorage.setItem(STORAGE_KEYS.accessToken, access);
    window.localStorage.setItem(STORAGE_KEYS.refreshToken, refresh);
    window.localStorage.setItem(STORAGE_KEYS.idToken, id);
  } catch {
    // localStorage unavailable — tokens remain in memory only
  }
}

function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  idToken = null;

  try {
    window.localStorage.removeItem(STORAGE_KEYS.accessToken);
    window.localStorage.removeItem(STORAGE_KEYS.refreshToken);
    window.localStorage.removeItem(STORAGE_KEYS.idToken);
  } catch {
    // localStorage unavailable — in-memory tokens already cleared
  }
}

/** Returns true when we have a cached access token (doesn't validate it). */
function hasToken(): boolean {
  return accessToken !== null;
}

export const tokenStore = {
  getAccessToken,
  getRefreshToken,
  getIdToken,
  setTokens,
  clearTokens,
  hasToken,
};
